// org scoping handled by RLS + project_id filter
import { supabase } from '@/lib/supabase';
import { SeoChangelog } from '@/types/api/seo';

function mapRow(row: any): SeoChangelog {
  return {
    id: row.id,
    organizationId: row.organization_id,
    projectId: row.project_id,
    pageId: row.page_id,
    taskId: row.task_id,
    agentId: row.agent_id,
    pageUrl: row.page_url,
    fieldName: row.field_name,
    oldValue: row.old_value,
    newValue: row.new_value,
    appliedAt: row.applied_at,
    appliedBy: row.applied_by,
    shadow: row.shadow,
    revertedAt: row.reverted_at,
    revertedBy: row.reverted_by,
    revertOf: row.revert_of,
    llmQualityScore: row.llm_quality_score != null ? Number(row.llm_quality_score) : null,
    validationPassed: row.validation_passed,
    validationErrors: row.validation_errors,
    createdAt: row.created_at,
  };
}

const seoChangelogService = {
  async getByProject(
    projectId: string,
    options?: {
      limit?: number;
      offset?: number;
      agentId?: string;
      pageId?: string;
      shadow?: boolean;
    }
  ): Promise<SeoChangelog[]> {
    // org scoping via RLS
    let query = supabase
      .from('seo_changelog')
      .select('*')
      .eq('project_id', projectId)
      .order('applied_at', { ascending: false });

    // org scoping via RLS
    if (options?.agentId) query = query.eq('agent_id', options.agentId);
    if (options?.pageId) query = query.eq('page_id', options.pageId);
    if (options?.shadow !== undefined) query = query.eq('shadow', options.shadow);
    if (options?.limit) query = query.limit(options.limit);
    if (options?.offset)
      query = query.range(options.offset, options.offset + (options.limit ?? 50) - 1);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async revert(changelogId: string): Promise<SeoChangelog> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get the original change
    const { data: original, error: fetchErr } = await supabase
      .from('seo_changelog')
      .select('*')
      .eq('id', changelogId)
      .single();

    if (fetchErr) throw fetchErr;
    if (!original) throw new Error('Changelog entry not found');
    if (original.reverted_at) throw new Error('Already reverted');

    // org scoping via RLS

    // Mark original as reverted
    await supabase
      .from('seo_changelog')
      .update({ reverted_at: new Date().toISOString(), reverted_by: user.id })
      .eq('id', changelogId);

    // Create revert entry (swap old/new values)
    const { data: revertEntry, error: insertErr } = await supabase
      .from('seo_changelog')
      .insert({
        organization_id: original.organization_id,
        project_id: original.project_id,
        page_id: original.page_id,
        task_id: original.task_id,
        agent_id: original.agent_id,
        page_url: original.page_url,
        field_name: original.field_name,
        old_value: original.new_value,
        new_value: original.old_value,
        applied_by: 'manual',
        shadow: false,
        revert_of: changelogId,
      })
      .select()
      .single();

    if (insertErr) throw insertErr;
    return mapRow(revertEntry);
  },

  async bulkRevert(changelogIds: string[]): Promise<number> {
    let reverted = 0;
    for (const id of changelogIds) {
      try {
        await this.revert(id);
        reverted += 1;
      } catch {
        // Skip already-reverted or missing entries
      }
    }
    return reverted;
  },
};

export default seoChangelogService;
